import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Checkbox, ModalConfirm } from '..';
import {
    getParticipantByIds,
    isAccessRestrictionOrganizationAllowedSelector,
    isConferenceAccessLevelPublicSelector,
} from '../../../pages/call/selectors';
import { ParticipantID } from '../../../pages/call/types';
import { kickPeer } from '../../features/moderation/slice';
import { i18n } from '../../helpers/i18n';
import css from './index.css';

interface ModalKickConfirmProps {
    onClose(): void;
    participantId: ParticipantID;
}

export const ModalKickConfirm: FunctionComponent<ModalKickConfirmProps> = ({ onClose, participantId }) => {
    const dispatch = useDispatch();
    const participantsByIds = useSelector(getParticipantByIds);
    const displayName = participantsByIds[participantId]?.displayName || '';
    const isConferenceAccessLevelPublic = useSelector(isConferenceAccessLevelPublicSelector);
    const isAccessRestrictionOrganizationAllowed = useSelector(isAccessRestrictionOrganizationAllowedSelector);

    const [withBan, setWithBan] = useState(false);
    const handleChange = useCallback(() => {
        setWithBan((value) => !value);
    }, [setWithBan]);

    const handleConfirm = useCallback(() => {
        onClose();
        dispatch(
            kickPeer({
                peerId: participantId,
                withBan,
            })
        );
    }, [withBan, dispatch, participantId, onClose]);

    const content = useMemo(() => {
        if (!isConferenceAccessLevelPublic) {
            return (
                <>
                    <div className={css.spaceBottom}>
                        {i18n('moderation', 'modal-kick-confirm-text', { name: displayName })}
                    </div>
                    <div>
                        <Checkbox
                            label={i18n('moderation', 'modal-kick-confirm-checkbox-label')}
                            size="md"
                            onChange={handleChange}
                            checked={withBan}
                            view="orb"
                        ></Checkbox>
                    </div>
                </>
            );
        }

        if (isAccessRestrictionOrganizationAllowed) {
            return (
                <>
                    <div className={css.spaceBottom}>
                        {i18n('moderation', 'modal-kick-confirm-text-open', { name: displayName })}
                    </div>
                    <div>{i18n('moderation', 'modal-kick-confirm-text-open-ban')}</div>
                </>
            );
        }

        return i18n('moderation', 'modal-kick-confirm-text', { name: displayName });
    }, [isConferenceAccessLevelPublic, isAccessRestrictionOrganizationAllowed, displayName, handleChange, withBan]);

    return (
        <ModalConfirm
            onClose={onClose}
            onConfirm={handleConfirm}
            headerText={i18n('moderation', 'modal-kick-confirm-title')}
            cancelButtonText={i18n('moderation', 'modal-kick-confirm-button-cancel')}
            confirmButtonText={i18n('moderation', 'modal-kick-confirm-button-confirm')}
        >
            {content}
        </ModalConfirm>
    );
};
